import { useEffect, useMemo, useRef } from 'react';

export type CacheEvent = {
  cacheName: string;
  version: number;
  changedAt: string;
  affectedUsers?: string[];
};

function getApiBaseUrl(): string {
  // In production we set VITE_API_URL to empty string to use relative paths.
  // Use nullish coalescing so empty string stays empty (do NOT fall back to localhost).
  return import.meta.env.VITE_API_URL ?? '';
}

/**
 * Subscribe to backend cache update events via SSE.
 * The backend emits `event: cache` with `{ cacheName, version, changedAt }`.
 */
export function useCacheEvents(options: {
  enabled?: boolean;
  cacheNames?: string[]; // if omitted, receive all
  onEvent: (event: CacheEvent) => void;
}) {
  const { enabled = true, cacheNames, onEvent } = options;
  const filter = useMemo(() => new Set(cacheNames || []), [cacheNames?.join('|')]);
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!enabled) return;

    const url = `${getApiBaseUrl()}/api/cache/events`;
    const es = new EventSource(url, { withCredentials: true } as any);

    const handler = (e: MessageEvent) => {
      try {
        const parsed = JSON.parse(e.data) as CacheEvent;
        if (cacheNames && cacheNames.length > 0 && !filter.has(parsed.cacheName)) return;
        onEventRef.current(parsed);
      } catch {
        // ignore
      }
    };

    es.addEventListener('cache', handler as any);
    es.onerror = () => {
      // EventSource will auto-reconnect; no-op.
    };

    return () => {
      es.removeEventListener('cache', handler as any);
      es.close();
    };
  }, [enabled, cacheNames?.join('|')]);
}


